/**
 * lib/optionsQual.ts
 * ─────────────────────────────────────────────────────────────
 * Options qualification engine — runs Phase 1 selection rules
 * against a fetched options chain and returns a structured
 * pass/warn/fail result for display in the scanner sidebar.
 *
 * Rules checked (in priority order):
 *  1. Spread ≤ $0.15          — liquidity gate (HARD FAIL if >$0.25)
 *  2. Open Interest ≥ 500     — liquidity gate (HARD FAIL if <100)
 *  3. Delta 0.25 – 0.45       — 1–2 OTM sweet spot
 *  4. Budget fit               — option ask ≤ account 2% risk budget
 *  5. Bid/ask mid > $0.50     — avoid sub-penny options
 * ─────────────────────────────────────────────────────────────
 */

import type { OptionsChain, OptionsContract, OptionsQualCheck } from '../types';

/**
 * Phase 1 options selection rules and their thresholds.
 * Centralised here so they're easy to tune as the strategy evolves.
 */
const RULES = {
  spreadMax:      0.15,   // hard fail above 0.25, warn above 0.15
  spreadHardFail: 0.25,
  oiMin:          500,    // hard fail below 100, warn below 500
  oiHardFail:     100,
  deltaMin:       0.25,
  deltaMax:       0.45,
  midMin:         0.50,   // avoid options so cheap they're lottery tickets
  budgetRiskPct:  0.02,   // 2% of account per trade
  accountDefault: 2000,   // fallback if not provided
};

/**
 * Evaluate all Phase 1 options rules for a single contract.
 * Returns a structured result for each rule.
 */
function evaluateContract(
  contract:  OptionsContract,
  accountBalance: number,
): {
  spreadOk:  boolean; spreadVal: number;
  oiOk:      boolean; oiVal:     number;
  deltaOk:   boolean; deltaVal:  number;
  budgetOk:  boolean; budgetVal: number;
  liquidOk:  boolean;
  passCount: number;
  failReasons: string[];
} {
  const maxRisk   = accountBalance * RULES.budgetRiskPct;
  const costPer   = contract.ask * 100; // 1 contract = 100 shares

  const spreadOk  = contract.spread <= RULES.spreadMax;
  const oiOk      = contract.openInterest >= RULES.oiMin;
  const deltaOk   = contract.delta >= RULES.deltaMin && contract.delta <= RULES.deltaMax;
  const budgetOk  = costPer <= maxRisk * 5;  // can afford at least 1 contract with reasonable risk
  const liquidOk  = contract.isLiquid; // spread + OI combined check from backend

  const passCount = [spreadOk, oiOk, deltaOk, budgetOk, liquidOk]
    .filter(Boolean).length;

  const failReasons: string[] = [];
  if (!spreadOk)  failReasons.push(`Spread $${contract.spread.toFixed(2)} > $${RULES.spreadMax}`);
  if (!oiOk)      failReasons.push(`OI ${contract.openInterest.toLocaleString()} < ${RULES.oiMin}`);
  if (!deltaOk)   failReasons.push(`Delta ${contract.delta.toFixed(2)} outside 0.25–0.45`);
  if (!budgetOk)  failReasons.push(`Cost $${costPer.toFixed(0)} exceeds budget`);
  if (!liquidOk && (spreadOk || oiOk))
                  failReasons.push('Combined liquidity insufficient');

  return {
    spreadOk, spreadVal: contract.spread,
    oiOk,     oiVal:     contract.openInterest,
    deltaOk,  deltaVal:  contract.delta,
    budgetOk, budgetVal: contract.ask,
    liquidOk,
    passCount,
    failReasons,
  };
}

/**
 * Main qualification function.
 * Takes a full options chain, finds the best 1-OTM call,
 * evaluates it against all Phase 1 rules, and returns
 * a structured OptionsQualCheck result.
 *
 * @param sym            - Ticker symbol
 * @param chain          - Full options chain from Tradier
 * @param accountBalance - Current account balance for budget check
 */
export function qualifyOptions(
  sym:            string,
  chain:          OptionsChain,
  accountBalance: number = RULES.accountDefault,
): OptionsQualCheck {
  const calls = chain.contracts.filter(c => c.optionType === 'call');

  if (!calls.length) {
    return {
      sym, status: 'error',
      bestStrike: null, bestExpiry: null,
      spreadOk: false, spreadVal: 0,
      oiOk: false,     oiVal: 0,
      deltaOk: false,  deltaVal: 0,
      budgetOk: false, budgetVal: 0,
      liquidOk: false,
      passCount: 0,
      failReasons: ['No call options available'],
      checkedAt: new Date().toISOString(),
    };
  }

  // Find the ideal 1-OTM call: prefer contracts with delta 0.30–0.40,
  // fall back to nearest OTM if nothing in the ideal delta range
  const underlyingPrice = chain.underlyingPrice;

  // Candidates: OTM calls (strike > underlying price)
  const otmCalls = calls
    .filter(c => c.strike > underlyingPrice && c.ask > RULES.midMin)
    .sort((a, b) => {
      // Sort by closeness to ideal delta 0.35
      const idealDelta = 0.35;
      return Math.abs(a.delta - idealDelta) - Math.abs(b.delta - idealDelta);
    });

  // Primary candidate: best delta fit
  const best = otmCalls[0] ?? calls[Math.floor(calls.length / 2)];
  if (!best) {
    return {
      sym, status: 'error',
      bestStrike: null, bestExpiry: null,
      spreadOk: false, spreadVal: 0,
      oiOk: false,     oiVal: 0,
      deltaOk: false,  deltaVal: 0,
      budgetOk: false, budgetVal: 0,
      liquidOk: false,
      passCount: 0,
      failReasons: ['No suitable OTM call found'],
      checkedAt: new Date().toISOString(),
    };
  }

  const result = evaluateContract(best, accountBalance);

  // Determine overall status
  // FAIL: any hard failure (terrible spread, no OI, way over budget)
  // WARN: some issues but tradeable (spread slightly wide, OI borderline)
  // PASS: all 5 rules pass cleanly
  const hasHardFail =
    best.spread > RULES.spreadHardFail ||
    best.openInterest < RULES.oiHardFail;

  const status: OptionsQualCheck['status'] =
    hasHardFail          ? 'fail' :
    result.passCount >= 5 ? 'pass' :
    result.passCount >= 3 ? 'warn' : 'fail';

  return {
    sym,
    status,
    bestStrike: best.strike,
    bestExpiry: chain.expirations[0] ?? null,
    spreadOk:  result.spreadOk,  spreadVal: result.spreadVal,
    oiOk:      result.oiOk,      oiVal:     result.oiVal,
    deltaOk:   result.deltaOk,   deltaVal:  result.deltaVal,
    budgetOk:  result.budgetOk,  budgetVal: result.budgetVal,
    liquidOk:  result.liquidOk,
    passCount: result.passCount,
    failReasons: result.failReasons,
    checkedAt: new Date().toISOString(),
  };
}

/**
 * Human-readable summary label for a qual check result.
 * Used in tooltips and the sidebar badge.
 */
export function qualSummaryLabel(check: OptionsQualCheck): string {
  if (check.status === 'loading') return 'Checking options…';
  if (check.status === 'error')   return 'Options unavailable';
  if (check.status === 'pass')    return `$${check.bestStrike} call ✓ all rules pass`;
  if (check.status === 'warn')    return `$${check.bestStrike} call ⚠ ${check.passCount}/5 rules`;
  return `$${check.bestStrike ?? '—'} call ✗ ${check.failReasons[0] ?? 'fails rules'}`;
}
